import type { AssetsSegmentElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type {
  MlPersonByAssetAndVersionNullable,
  MlPersonNullable,
} from '@iconik-io/iconik-api/src/services/ml';

import type { PopoverProps } from './faces_bounding_boxes_draw/face_popover_content';
import type { FaceDrawing } from './faces_bounding_boxes_draw/faces_bounding_boxes_draw';
import type { FaceBoundingBox } from './selectors';

const STATUS_ORDER = new Map([
  ['UNCONFIRMED', 0],
  ['NEW', 1],
  ['USER_CONFIRMED', 2],
  ['SYSTEM_CONFIRMED', 3],
]);

/** Compare person a's status to person b' according to STATUS_ORDER. */
export const comparePersonStatus = (
  a: MlPersonByAssetAndVersionNullable,
  b: MlPersonByAssetAndVersionNullable,
) => {
  const aIndex = STATUS_ORDER.get(getStatus(a)) ?? 0;
  const bIndex = STATUS_ORDER.get(getStatus(b)) ?? 0;

  return aIndex - bIndex;
};

/** Compare person a's name to person b' in user locale */
export const comparePersonNames = (
  a: MlPersonByAssetAndVersionNullable,
  b: MlPersonByAssetAndVersionNullable,
) => {
  return (a.name ?? '').localeCompare(b.name ?? '');
};

/** Return first segment (based on start time) */
export const findFirstPersonSegment = (
  segments: AssetsSegmentElasticSchemaWithIdNullable[] | undefined,
) => {
  if (!segments) {
    return null;
  }

  let result: AssetsSegmentElasticSchemaWithIdNullable | null = null;
  for (const segment of segments) {
    if (
      (segment.time_start_milliseconds ?? Infinity) <
      (result?.time_start_milliseconds ?? Infinity)
    ) {
      result = segment;
    }
  }
  return result;
};

export const getStatus = (
  instance:
    | PopoverProps
    | MlPersonByAssetAndVersionNullable
    | FaceBoundingBox
    | FaceDrawing
    | MlPersonNullable,
) => {
  const nameNotSet = instance.name === null;
  if (nameNotSet) {
    if (
      instance.status === 'USER_CONFIRMED' ||
      instance.status === 'SYSTEM_CONFIRMED'
    ) {
      return 'NEW';
    }
  }

  return instance.status;
};

export const groupDetectedPersonsCountByStatus = (
  detectedPersons: MlPersonByAssetAndVersionNullable[],
) =>
  detectedPersons.reduce(
    (acc, person) => {
      switch (getStatus(person)) {
        case 'UNCONFIRMED':
          acc.unconfirmed += 1;
          break;
        case 'NEW':
          acc.new += 1;
          break;
        case 'USER_CONFIRMED':
        case 'SYSTEM_CONFIRMED':
          acc.confirmed += 1;
          break;
        default:
          break;
      }
      return acc;
    },
    { unconfirmed: 0, new: 0, confirmed: 0 },
  );

/** Compare person a to person b according to the first segments time and person bounding box position.*/
export const comparePersonSegments = (
  segments: Record<
    string,
    AssetsSegmentElasticSchemaWithIdNullable[] | undefined
  >,
  a: MlPersonByAssetAndVersionNullable,
  b: MlPersonByAssetAndVersionNullable,
) => {
  const personASegment = findFirstPersonSegment(segments[a.id]);
  const personBSegment = findFirstPersonSegment(segments[b.id]);
  const timeDifference =
    (personASegment?.time_start_milliseconds ?? Infinity) -
    (personBSegment?.time_start_milliseconds ?? Infinity);
  if (timeDifference !== 0) {
    return timeDifference;
  }

  const personAPosition =
    personASegment?.face_bounding_boxes?.[0].bounding_box?.[0] ?? 0;
  const personBPosition =
    personBSegment?.face_bounding_boxes?.[0].bounding_box?.[0] ?? 0;
  return personAPosition - personBPosition;
};

/** Compare person a and b according to status, name and order of appearance in *segments*. */
export const comparePersons = (
  segments: Record<
    string,
    AssetsSegmentElasticSchemaWithIdNullable[] | undefined
  >,
  personA: MlPersonByAssetAndVersionNullable,
  personB: MlPersonByAssetAndVersionNullable,
) => {
  const statusDifference = comparePersonStatus(personA, personB);
  if (statusDifference !== 0) {
    return statusDifference;
  }

  const nameDifference = comparePersonNames(personA, personB);
  if (nameDifference !== 0) {
    return nameDifference;
  }

  const segmentDifference = comparePersonSegments(segments, personA, personB);
  if (segmentDifference !== 0) {
    return segmentDifference;
  }

  return 0;
};

export const isFaceRecognitionUnsupportedOnScreen = (breakpoint: number) =>
  breakpoint < 2;
